// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { Session } from "@insite/client-framework/Services/SessionService";

export const detectIsAuthenticated = (session: Session) =>
    session && (session.isAuthenticated || session.rememberMe) && !session.isGuest;

export const scrollToTop = (withAnimation = true, animationType: "smooth" | "auto" | undefined = "smooth") => {
    if (withAnimation) {
        window && window.scroll({ top: 0, left: 0, behavior: animationType });
    } else {
        window && window.scrollTo(0, 0);
    }
};

export const isTextWrap = (element: HTMLElement) => {
    if (!element) {
        return false;
    }
    const item = element.cloneNode(true) as any;
    item.style.visibility = "hidden";
    document.body.appendChild(item);
    const clnHeight = item.clientHeight;
    item.parentNode.removeChild(item);
    return clnHeight < element.clientHeight;
};

// To use it:
// 1. create let _eventHandlers = {}; globaly inside the file
// 2. appened xAddEventListener response to _eventHandlers
export const xAddEventListener = (
    node: any,
    events: string[],
    handler: any,
    capture = false,
    _eventHandlers: any = {},
) => {
    const customEventHandlers: any = [];
    events.forEach(event => {
        let _eventHandler = [];
        if (!(event in _eventHandlers)) {
            _eventHandler = [];
        }
        // here we track the events and their nodes (note that we cannot
        // use node as Object keys, as they'd get coerced into a string
        _eventHandler.push({ node, handler, capture });
        node.addEventListener(event, handler, capture);
        customEventHandlers.push(_eventHandler);
    });
    return customEventHandlers;
};

export const xRemoveAllEventListeners = (targetNode: any, events: string[], _eventHandlers: any = {}) => {
    if (targetNode) {
        // remove listeners from the matching nodes
        events.forEach(event => {
            _eventHandlers[event]
                ?.filter(({ node }: { node: any }) => node === targetNode)
                ?.forEach(({ node, handler, capture }: { node: any; handler: any; capture: any }) =>
                    node.removeEventListener(event, handler, capture),
                );

            // update _eventHandlers global
            _eventHandlers[event] = _eventHandlers[event].filter(({ node }: { node: any }) => node !== targetNode);
        });
    } else {
        events.forEach(event => {
            _eventHandlers[event]?.forEach(({ node, handler, capture }: { node: any; handler: any; capture: any }) =>
                node.removeEventListener(event, handler, capture),
            );
        });
    }
};

export const isEventSupported = (eventName: string) => {
    let el: any = document.createElement("div");
    eventName = `on${eventName}`;
    let isSupported = eventName in el;
    if (!isSupported) {
        el.setAttribute(eventName, "return;");
        isSupported = typeof el[eventName] === "function";
    }
    el = null;
    return isSupported;
};

export const isIntegerNumber = (value: string) => {
    return !/[\+\-\.\,]$/.test(value);
};

export const spaceValidation = (value: string) => {
    return value.indexOf(" ") !== 0 && value.replace(/\s/g, "").length;
};

export const isPostalCodeValid = (value: string) => {
    // This Regex Handle USA and Canada.
    const regexp = new RegExp("^([0-9]{5}(-[0-9]{4})?|[A-Z][0-9][A-Z] ?[0-9][A-Z][0-9])$");
    return regexp.test(value);
};

export const escapeDoubleQuotes = (str: string) => {
    return str.replace(/\\([\s\S])|(")/g, "\\$1$2");
};

export const parseBoolean = (value?: string) => (value ? value?.toLowerCase() === "true" : false);

/**
 * Helper function to export data to csv file
 * @param fields
 * @param data
 * @param filename
 */
export const exportToCSV = async (fields: string[], data: (string | number | null)[][], filename?: string) => {
    const dataForUnparse = {
        fields,
        data,
    };

    const { unparse } = await import(/* webpackChunkName: "papaparse" */ "papaparse");
    const csv = unparse(dataForUnparse);
    const csvBlob = new Blob([`\uFEFF${csv}`], { type: "text/csv;charset=utf-8;" });
    const fileName = `${filename || "csv_exported_file"}_${Math.random() * 10000}.csv`;
    const downloadLink = document.createElement("a");
    downloadLink.href = window.URL.createObjectURL(csvBlob);
    downloadLink.setAttribute("download", fileName);
    downloadLink.click();
};
